import React from 'react';
import ComboBox from 'Components/ComboBox/ComboBox';
import InputField from 'Components/Input/InputField';
import { parseISO } from 'date-fns';
import TextAreaField from 'Components/TextAreaField/TextAreaField';
import Panel from 'Components/Panel/Panel';
import ReactDataPicker from 'Components/ReactDataPicker/ReactDataPickerComp';
import ReactDataPickerComp from 'Components/ReactDataPicker/ReactDataPickerComp';
import MandatoryIconField from 'Components/MandatoryIconField';
import SearchableLookup from 'Components/SearchableLookup/SearchableLookup';

const AutomaticallyListedAttributes = ({
    list,
    attributeList,
    readOnlyAttributes,
    height = '100%'
}) => {
    if (!list || list.length === 0 || !list[0].constructorList) {
        return;
    }

    const sortedList = list[0].constructorList.sort((a, b) => {
        const presentationSequenceA = a.presentationSequence;
        const presentationSequenceB = b.presentationSequence;
        return presentationSequenceA - presentationSequenceB;
    });

    const mainStyle = {
        flexWrap: 'wrap',
        height: height,
        overflow: 'auto',
        alignContent: 'flex-start',
        padding: '4px'
    };

    const row = {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        flexWrap: 'wrap'
    }

    let rows = [];
    let currentRow = [];

    sortedList.forEach(attribute => {
        const attributeDataBackend = {
            field: attribute.field,
            label: attribute.label,
            isMandatory: attribute.isMandatory,
            columnType: attribute.columnType,
            readOnly: attribute.readOnly,
            isVisible: attribute.visible == 'N' ? false : true,
            mask: attribute.mask,
            placeholder: attribute.placeHolder,
            dataType: attribute.dataType
        };

        if (!attributeDataBackend.label) {
            return;
        }

        const attributeDataFrontend = attributeList.find(obj => obj.name === attributeDataBackend.field) || {};
        const readOnlyField = getReadOnlyField(readOnlyAttributes, attributeDataFrontend.readOnly, attributeDataBackend.readOnly);
        const inputField = getComponentField(attributeDataBackend, attributeDataFrontend, readOnlyField);

        const visible =
            attributeDataFrontend?.isVisible === false || attributeDataFrontend?.isVisible == 'N'
                ? false
                : attributeDataBackend?.isVisible === false || attributeDataBackend?.isVisible == 'N'
                    ? false
                    : true;


        if (inputField && visible) {
            currentRow.push(
                <div key={attributeDataBackend.field} id='attribute-control'>
                    <span className='span-role-control' style={{ marginRight: '5px', display: 'flex', maxHeight: '18px' }}>{getNameField(attributeDataFrontend, attributeDataBackend)}</span>
                    {inputField}
                </div>
            );

            if (attributeDataFrontend.newRow) {
                rows.push(<div key={`row-${rows.length}`} style={row} className="attribute-row">{currentRow}</div>);
                currentRow = [];
            }
        }
    });

    if (currentRow.length > 0) {
        rows.push(<div key={`row-${rows.length}`} style={row} className="attribute-row">{currentRow} </div>);
    }

    return (
        <Panel style={mainStyle} className={'scrollable-content'}>
            {rows}
        </Panel>
    );
};

function getNameField(attributeDataFrontend, attributeDataBackend) {
    const baseText = attributeDataFrontend.customMessage
        ? attributeDataFrontend.customMessage
        : attributeDataBackend.label;

    return (
        <>
            {baseText}
            {(attributeDataBackend.isMandatory === 'S' || attributeDataFrontend.isMandatory === 'S') && mandatoryField()}
        </>
    );
}

function mandatoryField() {
    return <MandatoryIconField />
}

function getReadOnlyField(readOnlyAttributes, readOnlyFrontend, readOnlyBackend) {
    if (readOnlyFrontend) {
        return true
    } else if (!readOnlyFrontend && readOnlyAttributes) {
        return true;
    } else if (!readOnlyFrontend && !readOnlyAttributes && readOnlyBackend === 'S') {
        return true
    }
    return false
}

function getCheckboxSetter(attributeDataFrontend) {
    if (attributeDataFrontend.checkboxSetter) {
        return attributeDataFrontend
    } else {
        return (
            function checkboxSetter(value) {
                attributeDataFrontend.setter(value === 'S' ? 'N' : 'S')
            }
        )
    }
}

function convertLocalTimeToInstant(localTime) {
    const isLocalTimeFormat = /^\d{2}:\d{2}:\d{2}$/.test(localTime);

    if (!localTime || !isLocalTimeFormat) {
        return localTime
    }

    const today = new Date();
    const [hours, minutes, seconds] = localTime.split(':').map(Number);
    today.setHours(hours, minutes, seconds, 0);
    return today;
}

const handleSetAttribute = (value, attributeDataBackend, attributeDataFrontend) => {
    if (attributeDataBackend?.dataType?.toUpperCase() === "NUMBER") {
        if (value) {
            value = String(value).replace(",", ".");
        }

        if (!/^\d*\.?\d*$/.test(value)) {
            return;
        }
    }

    attributeDataFrontend?.setter(value);
};

function getComponentField(attributeDataBackend, attributeDataFrontend, readOnlyField) {
    const width = attributeDataFrontend.width ? attributeDataFrontend.width : '150px';
    switch (attributeDataBackend.columnType.toUpperCase()) {
        case 'INPUT':
            if (attributeDataFrontend.searchMethod) {
                return (
                    <SearchableLookup
                        setAttribute={attributeDataFrontend.setter}
                        searchMethod={attributeDataFrontend.searchMethod}
                        onSelect={attributeDataFrontend.onSelect}
                        value={attributeDataFrontend.value}
                        readOnly={readOnlyField}
                        disabled={readOnlyField}
                        style={{ ...attributeDataFrontend.style, width: width }}
                        onBlur={attributeDataFrontend.onBlur}
                        placeholder={attributeDataBackend.placeholder}
                    />
                )
            }
            return (
                <InputField
                    setAttribute={(value) => handleSetAttribute(value, attributeDataBackend, attributeDataFrontend)}
                    value={attributeDataFrontend.value}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    style={{ ...attributeDataFrontend.style, width: width }}
                    mask={attributeDataBackend.mask}
                    onBlur={attributeDataFrontend.onBlur}
                    focus={attributeDataFrontend.focus}
                    dataType={attributeDataBackend.dataType}
                />
            )
        case 'COMBOBOX':
            return (
                <ComboBox setAttribute={attributeDataFrontend.setter}
                    optionsData={attributeDataFrontend.comboboxOptions}
                    value={attributeDataFrontend.value}
                    style={{ ...attributeDataFrontend.style, width: width }}
                    defaultValue={attributeDataFrontend.style}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    onBlur={attributeDataFrontend.onBlur}
                />
            )
        case 'CHECKBOX':
            return (
                <InputField
                    setAttribute={getCheckboxSetter(attributeDataFrontend)}
                    value={attributeDataFrontend.value}
                    checked={attributeDataFrontend.value === 'S'}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    type="checkbox"
                    style={{ ...attributeDataFrontend.style, cursor: 'pointer', width: width }}
                    onBlur={attributeDataFrontend.onBlur}
                />
            )
        case 'TEXT-AREA':
            return (
                <TextAreaField
                    setAttribute={attributeDataFrontend.setter}
                    value={attributeDataFrontend.value}
                    type="text"
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    style={{ ...attributeDataFrontend.style, width: width }}
                    onBlur={attributeDataFrontend.onBlur}
                />
            )
        case 'DATE':
            return (
                <ReactDataPickerComp
                    selected={attributeDataFrontend.value}
                    onChange={(date) => { attributeDataFrontend.setter(date) }}
                    className='cost-fix-width-date cursor-pointer'
                    showMonthYearPicker={attributeDataBackend.mask.toUpperCase() === 'MM/YYYY'}
                    showTimeSelect={attributeDataBackend.mask.toUpperCase() === 'HH:MM'}
                    showTimeSelectOnly={attributeDataBackend.mask.toUpperCase() === 'HH:MM'}
                    timeFormat={attributeDataBackend.mask}
                    placeholderText={attributeDataBackend.placeholder}
                    dateFormat={attributeDataBackend.mask}
                    timeCaption='Horas'
                    style={{ ...attributeDataFrontend.style, width: '150px' }}
                    readOnly={readOnlyField}
                    disabled={readOnlyField}
                    onBlur={attributeDataFrontend.onBlur}
                    setter={attributeDataFrontend.setter}
                />
            )
        default:
            return null
    }
}

export default AutomaticallyListedAttributes;
